// src/services/api/stockAdjustment.js
import { apiClient } from './base';

/**
 * Get user's accessible warehouses for stock adjustments
 */
export const getUserWarehouses = async () => {
  try {
    const response = await apiClient.get('/stock-adjustments/warehouses/accessible');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get today's draft stock adjustments for a warehouse
 * @param {number} warehouseId - Warehouse ID
 */
export const getTodaysAdjustments = async (warehouseId) => {
  try {
    const response = await apiClient.get('/stock-adjustments/todays', {
      params: { warehouseId }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Search products for stock adjustment
 * @param {Object} params - Search parameters
 * @param {number} params.warehouseId - Warehouse ID (required)
 * @param {string} params.search - Search term
 * @param {number} params.page - Page number
 * @param {number} params.limit - Results per page
 * @param {string} params.type - Product type filter (simple/variable)
 */
export const searchProductsForAdjustment = async (params) => {
  try {
    const response = await apiClient.get('/stock-adjustments/products/search', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Add items to stock adjustment
 * @param {Object} data - Adjustment data
 * @param {number} data.warehouseId - Warehouse ID (required)
 * @param {number} data.adjustmentId - Adjustment ID (optional)
 * @param {boolean} data.forceNew - Force new adjustment (optional)
 * @param {Array} data.items - Items array (required)
 */
export const addItemsToAdjustment = async (data) => {
  try {
    const response = await apiClient.post('/stock-adjustments/items', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update stock adjustment item
 * @param {number} itemId - Adjustment item ID
 * @param {Object} data - Update data
 * @param {number} data.physicalCount - Physical count (optional)
 * @param {string} data.notes - Notes (optional)
 */
export const updateAdjustmentItem = async (itemId, data) => {
  try {
    const response = await apiClient.put(`/stock-adjustments/items/${itemId}`, data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete stock adjustment item
 * @param {number} itemId - Adjustment item ID
 */
export const deleteAdjustmentItem = async (itemId) => {
  try {
    const response = await apiClient.delete(`/stock-adjustments/items/${itemId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Finalize stock adjustment
 * @param {number} adjustmentId - Adjustment ID
 */
export const finalizeStockAdjustment = async (adjustmentId) => {
  try {
    const response = await apiClient.post(`/stock-adjustments/${adjustmentId}/finalize`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get stock adjustment by ID
 * @param {number} adjustmentId - Adjustment ID
 */
export const getStockAdjustmentById = async (adjustmentId) => {
  try {
    const response = await apiClient.get(`/stock-adjustments/${adjustmentId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all stock adjustments with filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.limit - Results per page
 * @param {string} params.search - Search term
 * @param {number} params.warehouseId - Warehouse ID filter
 * @param {string} params.status - Status filter (draft/finalized)
 * @param {string} params.dateFrom - Date from filter
 * @param {string} params.dateTo - Date to filter
 */
export const getAllStockAdjustments = async (params = {}) => {
  try {
    const response = await apiClient.get('/stock-adjustments', { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};